import { useEffect, useRef } from 'react';
import { Browser } from '@capacitor/browser';
import { Capacitor } from '@capacitor/core';
import {
  PushNotifications,
  type ActionPerformed,
  type PushNotificationSchema,
  type Token,
} from '@capacitor/push-notifications';
import type { PluginListenerHandle } from '@capacitor/core';
import { useQueryClient } from '@tanstack/react-query';
import { navigateTo } from '@shared/lib/navigation';
import { useAuthStore } from '@shared/stores/authStore';
import { useSettingsStore } from '@shared/stores/settingsStore';
import {
  markNotificationRead,
  registerPushToken,
  unregisterPushToken,
  type NotificationType,
} from './api';
import { getNotificationTarget } from './notificationTargets';

const APP_ID = 'ip4estates';
const PUSH_TOKEN_STORAGE_KEY = 'ip4estates_push_token';

export function NativePushBridge(): null {
  const queryClient = useQueryClient();
  const isAuthed = useAuthStore((state) => state.isAuthenticated());
  const tenantId = useAuthStore((state) => state.tenantId);
  const pushEnabled = useSettingsStore((state) => state.pushEnabled);
  const lastRegisteredToken = useRef<string | null>(null);

  useEffect(() => {
    if (!Capacitor.isNativePlatform() || !isAuthed || !tenantId) return;

    let cancelled = false;
    const handles: PluginListenerHandle[] = [];

    const addListenersAndRegister = async (): Promise<void> => {
      if (!pushEnabled) {
        lastRegisteredToken.current = null;
        await unregisterStoredToken();
        return;
      }

      const permission = await ensurePushPermission();
      useSettingsStore.getState().setPushEnabled(permission === 'granted');
      if (permission !== 'granted') {
        lastRegisteredToken.current = null;
        await unregisterStoredToken();
        return;
      }
      if (cancelled) return;

      handles.push(
        await PushNotifications.addListener('registration', (token) => {
          void onRegistered(token);
        }),
      );
      handles.push(
        await PushNotifications.addListener('registrationError', (error) => {
          console.warn('[push] registration failed:', error);
        }),
      );
      handles.push(
        await PushNotifications.addListener('pushNotificationReceived', (notification) => {
          onNotificationReceived(notification);
        }),
      );
      handles.push(
        await PushNotifications.addListener('pushNotificationActionPerformed', (action) => {
          void onNotificationActionPerformed(action);
        }),
      );

      await PushNotifications.register();
    };

    const onRegistered = async (token: Token): Promise<void> => {
      const value = token.value?.trim();
      if (!value || value === lastRegisteredToken.current) return;

      await registerPushToken({
        token: value,
        platform: Capacitor.getPlatform(),
        app_id: APP_ID,
      });
      lastRegisteredToken.current = value;
      rememberToken(value);
    };

    const onNotificationReceived = (notification: PushNotificationSchema): void => {
      void queryClient.invalidateQueries({ queryKey: ['notifications'] });
      console.info('[push] notification received:', notification.title ?? notification.data?.title);
    };

    const onNotificationActionPerformed = async (action: ActionPerformed): Promise<void> => {
      void queryClient.invalidateQueries({ queryKey: ['notifications'] });

      const data = action.notification.data ?? {};
      const notificationId = typeof data.notification_id === 'string' ? data.notification_id : null;
      if (notificationId) {
        await markNotificationRead(notificationId).catch(() => { /* non-fatal */ });
      }

      const target = getNotificationTarget({
        action_url: typeof data.action_url === 'string' ? data.action_url : undefined,
        notification_type: normalizeNotificationType(data.notification_type),
        related_id: typeof data.related_id === 'string' ? data.related_id : undefined,
      });

      if (target?.kind === 'internal') {
        navigateTo(target.path);
      } else if (target?.kind === 'external') {
        await Browser.open({ url: target.url }).catch(() => {
          window.open(target.url, '_blank', 'noopener,noreferrer');
        });
      }
    };

    void addListenersAndRegister().catch((error) => {
      console.warn('[push] setup failed:', error);
    });

    return () => {
      cancelled = true;
      for (const handle of handles) {
        void handle.remove();
      }
    };
  }, [isAuthed, pushEnabled, queryClient, tenantId]);

  return null;
}

async function ensurePushPermission(): Promise<'granted' | 'denied' | 'prompt' | 'prompt-with-rationale'> {
  const current = await PushNotifications.checkPermissions();
  if (current.receive === 'prompt' || current.receive === 'prompt-with-rationale') {
    const requested = await PushNotifications.requestPermissions();
    return requested.receive;
  }
  return current.receive;
}

function normalizeNotificationType(value: unknown): NotificationType {
  return typeof value === 'string' && value.trim()
    ? (value as NotificationType)
    : 'general';
}

function rememberToken(token: string): void {
  try {
    localStorage.setItem(PUSH_TOKEN_STORAGE_KEY, token);
  } catch {
    // Non-sensitive cache only; failing to persist should not break push.
  }
}

async function unregisterStoredToken(): Promise<void> {
  let token: string | null = null;
  try {
    token = localStorage.getItem(PUSH_TOKEN_STORAGE_KEY);
  } catch {
    token = null;
  }

  if (!token) return;

  await unregisterPushToken(token).catch(() => { /* non-fatal */ });
  try {
    localStorage.removeItem(PUSH_TOKEN_STORAGE_KEY);
  } catch {
    // noop
  }
}
